import { useMemo } from "react";
import { motion } from "framer-motion";
import { Pencil } from "lucide-react";
import { colorForName, getInitials } from "@/components/card/SignersBubbles";
import type { Tables } from "@/integrations/supabase/types";

interface SignersStoriesBarProps {
  messages: Tables<"messages">[];
  contributions?: Tables<"contributions">[];
  seenKeys?: Set<string>;
  onSignerClick: (key: string) => void;
  onAddClick?: () => void;
  orientation?: "horizontal" | "vertical";
}

interface StoryPerson {
  key: string;
  name: string;
  avatar: string | null;
  hasMedia: boolean;
  messageCount: number;
  hasContribution: boolean;
}

function groupSigners(
  messages: Tables<"messages">[],
  contributions: Tables<"contributions">[]
): StoryPerson[] {
  const map = new Map<string, StoryPerson>();

  for (const msg of messages) {
    const key = msg.contributor_email || msg.contributor_name;
    const existing = map.get(key);
    const mediaUrl = msg.photo_url || msg.gif_url || msg.video_url || null;
    if (existing) {
      existing.messageCount++;
      if (!existing.avatar && mediaUrl) {
        existing.avatar = mediaUrl;
        existing.hasMedia = true;
      }
    } else {
      map.set(key, {
        key,
        name: msg.contributor_name,
        avatar: mediaUrl,
        hasMedia: !!mediaUrl,
        messageCount: 1,
        hasContribution: false,
      });
    }
  }

  for (const c of contributions) {
    if (c.status !== "completed") continue;
    const key = c.contributor_email || c.contributor_name;
    const person = map.get(key);
    if (person) person.hasContribution = true;
  }

  return Array.from(map.values());
}

export default function SignersStoriesBar({
  messages,
  contributions = [],
  seenKeys = new Set(),
  onSignerClick,
  onAddClick,
  orientation = "horizontal",
}: SignersStoriesBarProps) {
  const signers = useMemo(
    () => groupSigners(messages, contributions),
    [messages, contributions]
  );

  const isVertical = orientation === "vertical";

  return (
    <div className={isVertical ? "h-full overflow-y-auto scrollbar-hide" : "w-full overflow-x-auto scrollbar-hide"}>
      <div className={isVertical
        ? "flex flex-col items-center gap-3 px-2 py-4"
        : "flex items-start gap-3 px-4 pt-10 pb-3 min-w-max"
      }>
        {/* Sign CTA */}
        {onAddClick && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0, type: "spring", stiffness: 300, damping: 20 }}
            onClick={onAddClick}
            className="flex flex-col items-center gap-1 shrink-0 w-[76px]"
          >
            <div className="relative">
              <div className="w-[66px] h-[66px] rounded-full bg-muted/60 border-2 border-dashed border-primary/40 flex items-center justify-center">
                <Pencil size={22} className="text-primary" />
              </div>
            </div>
            <span className="text-[10px] text-muted-foreground font-medium leading-tight">
              Sign
            </span>
          </motion.button>
        )}

        {signers.map((person, i) => {
          const isSeen = seenKeys.has(person.key);
          return (
            <motion.button
              key={person.key}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                delay: (i + 1) * 0.04,
                type: "spring",
                stiffness: 300,
                damping: 20,
              }}
              onClick={() => onSignerClick(person.key)}
              className="flex flex-col items-center gap-1 shrink-0 w-[76px] group"
            >
              <div className="relative">
                {/* Gradient ring: colorful = unseen, grey = seen */}
                <div
                  className={`w-[72px] h-[72px] rounded-full p-[2.5px] ${
                    isSeen
                      ? "bg-gradient-to-tr from-muted-foreground/30 to-muted-foreground/10"
                      : "bg-gradient-to-tr from-primary via-pink-500 to-amber-400"
                  }`}
                >
                  <div className="w-full h-full rounded-full bg-background p-[2px]">
                    {person.avatar ? (
                      <img
                        src={person.avatar}
                        alt={person.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <div
                        className={`w-full h-full rounded-full flex items-center justify-center text-sm font-semibold ${colorForName(
                          person.name
                        )}`}
                      >
                        {getInitials(person.name)}
                      </div>
                    )}
                  </div>
                </div>
                {/* Contribution badge */}
                {person.hasContribution && (
                  <span className="absolute -bottom-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-amber-400 text-amber-900 flex items-center justify-center text-[8px] font-bold ring-2 ring-background px-1">
                    💰
                  </span>
                )}
              </div>
              <span className="text-[10px] text-muted-foreground font-medium max-w-[70px] truncate leading-tight group-hover:text-foreground transition-colors">
                {person.name.split(" ")[0]}
              </span>
            </motion.button>
          );
        })}

        {signers.length === 0 && !onAddClick && (
          <p className="text-xs text-muted-foreground py-2 px-2">No signatures yet</p>
        )}
      </div>
    </div>
  );
}
